import ContextErrorBanner from 'errors/components/ContextErrorBanner'
import ErrorContextProvider, { useErrorContext } from 'errors/components/ErrorContextProvider'
import { t } from 'localization'
import { isEmpty, isString, toFinite, toInteger } from 'lodash/fp'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { FC, useEffect, useState } from 'react'
import { SearchFiltersHumanizedRefinement } from 'search'
import { Button } from 'shared/components/Button'
import { Card } from 'shared/components/CardDeprecated'
import { SearchQA } from 'shared/dataAttributes'
import { colors } from 'shared/lib'
import styled from 'styled-components'

const TP = 'search.enums.search'
const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`

const NumberInput = styled.input.attrs({ type: 'number' })`
  width: 100%;
  height: 40px;
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
  padding: 15px 13px;
  margin-bottom: 15px;
`

const Label = styled.label`
  text-transform: uppercase;
  color: ${colors.FC2_GREY};
  margin-bottom: 5px;
  font-size: 10px;
`

interface PriceFilterProps {
  currentRefinement
  refine
  selectedValues: {
    min?: number
    max?: number
  }
  attribute: string
  baseAttribute: string
  currency: string
}

const PriceFilterContainer: FC<PriceFilterProps> = ({
  currentRefinement,
  refine,
  selectedValues,
  baseAttribute,
}) => {
  const initialMinPrice = currentRefinement.min
  const initialMaxPrice = currentRefinement.max

  const { setErrors } = useErrorContext()
  const [minPrice, setMinPrice] = useState(initialMinPrice)
  const [maxPrice, setMaxPrice] = useState(initialMaxPrice)

  useEffect(() => {
    setMaxPrice(selectedValues?.max)
    setMinPrice(selectedValues?.min)
  }, [selectedValues])

  const onPriceChange = (e, set) => {
    let value = e.target.value
    const number = toInteger(value)

    if (!isEmpty(value)) {
      if (number < 0) {
        value = Math.abs(number).toString() // For negative inputs
      } else {
        value = number.toString()
      }
    }

    set(value)
  }

  const isEmptyValue = (value) => {
    if (isString(value)) isEmpty(value)

    return value === undefined || value === null
  }

  const isPriceRangeInvalid = () =>
    !!minPrice && !!maxPrice && toFinite(minPrice) > toFinite(maxPrice)

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isPriceRangeInvalid()) {
      setErrors([
        {
          message: t(`${TP}.priceRangeInvalid`, "'From' price should be lower than 'To' price"),
        },
      ])
      return
    } else {
      setErrors([])
    }

    const isChanged = currentRefinement.min !== minPrice || currentRefinement.max !== maxPrice
    const isFormEmpty = isEmptyValue(minPrice) && isEmptyValue(maxPrice)
    const filterName = SearchFiltersHumanizedRefinement()[baseAttribute] || ''
    if (isFormEmpty) {
      sendTrackingEvent('FILTER_APPLY_TAP', {
        page: window?.location?.href,
        filter_name: filterName.toLowerCase(),
        filter_selection: `${minPrice}-${maxPrice}`,
      })
      refine({
        ...currentRefinement,
        min: selectedValues?.min,
        max: selectedValues?.max,
      })
      return
    }
    if (isChanged) {
      sendTrackingEvent('FILTER_APPLY_TAP', {
        page: window?.location?.href,
        filter_name: filterName.toLowerCase(),
        filter_selection: `${minPrice}-${maxPrice}`,
      })
      refine({
        ...currentRefinement,
        min: isEmptyValue(minPrice) ? selectedValues?.min : minPrice,
        max: isEmptyValue(maxPrice) ? selectedValues?.max : maxPrice,
      })
    }
  }

  const hasValues = !!minPrice || !!maxPrice

  return (
    <Card
      qaAttr={SearchQA.FilterPrice}
      title={SearchFiltersHumanizedRefinement()[baseAttribute]}
      isExpanded={hasValues}
    >
      <CardContent>
        <form onSubmit={handleSubmit}>
          <ContextErrorBanner />
          <Label>{t(`${TP}.from`, 'From')}</Label>
          <NumberInput
            data-qa={SearchQA.FilterPriceMinAmount}
            min={currentRefinement?.min}
            max={currentRefinement?.max}
            value={minPrice || ''}
            onChange={(e) => onPriceChange(e, setMinPrice)}
            data-testid="min-price-input"
          />
          <Label>{t(`${TP}.to`, 'To')}</Label>
          <NumberInput
            data-qa={SearchQA.FilterPriceMaxAmount}
            min={currentRefinement?.min}
            max={currentRefinement?.max}
            value={maxPrice || ''}
            onChange={(e) => onPriceChange(e, setMaxPrice)}
            data-testid="max-price-input"
          />
          <Button
            qaAttr={SearchQA.FilterPriceApplyButton}
            $fill
            buttonType="secondary"
            type="submit"
            uniqueLogEvent
          >
            {t(`${TP}.apply`, 'Apply')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

const PriceFilterProvider = (props) => (
  <ErrorContextProvider>
    <PriceFilterContainer {...props} />
  </ErrorContextProvider>
)

PriceFilterProvider.displayName = 'PriceFilterContainer'

export default PriceFilterContainer
